import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export type InboxItem = {
  id: string;
  title: string;
  detail: string;
  to: string;
  glyph: ReactNode;
  /** Lower sorts first. The queue is ordered by how much it needs a human. */
  rank: number;
};

/**
 * "Needs a human" — a ranked action queue, not a count.
 *
 * Each row is one click from the place it can be acted on. Ordering is by rank
 * (risk x confidence, staleness, blocked work), so the top of the list is the
 * thing to do next rather than an arbitrary page of a table.
 */
export default function ActionInbox({ items }: { items: InboxItem[] }) {
  const ranked = [...items].sort((a, b) => a.rank - b.rank);
  if (ranked.length === 0) {
    return (
      <p className="text-sm opacity-60">
        Nothing needs you — no verdicts to review, no gaps to graduate, nothing stale.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-base-300 rounded-lg bg-base-200">
      {ranked.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            className="flex items-center gap-3 px-3 py-2.5 hover:bg-base-300 rounded-lg"
          >
            {item.glyph}
            <span className="min-w-0">
              <span className="block font-medium truncate">{item.title}</span>
              <span className="block text-xs opacity-60 truncate">{item.detail}</span>
            </span>
            <span className="ml-auto text-xs opacity-50">Open →</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
